// Same pattern the generic-export job uses when fileNamingSchema is omitted.
export const DEFAULT_FILENAME_SCHEMA =
  '{DIRECTORY}/Wurk_{REPORT_ID}_{YEAR_4_DIGITS}{MONTH_LEADING_ZERO}{DAY_LEADING_ZERO}_{HOUR_24}{MINUTE}{SECOND}.csv';

const KRONOS_DEFAULT = {
  type: '1password_item',
  '1password_item_path': 'Client credentials/UKG - EnjoyWurkAdmin - apiuser'
};

const EMAIL_DEFAULT = {
  emailContacts: [],
  sendSuccessEmails: 'false'
};

export const directionForJobType = (jobType) =>
  jobType === 'generic-export' ? 'export' : 'import';

export const jobTypeForDirection = (direction) =>
  direction === 'export' ? 'generic-export' : 'generic-import';

export function defaultsFor(direction) {
  const shared = {
    companyName: '',
    kronos: { ...KRONOS_DEFAULT },
    sftp: {
      host: '',
      port: 22,
      username: '',
      password: ''
    },
    directory: '',
    emailConfig: {
      emailContacts: [],
      sendSuccessEmails: EMAIL_DEFAULT.sendSuccessEmails
    }
  };

  if (direction === 'export') {
    return {
      ...shared,
      reportID: '',
      isExportReport: false,
      fileNamingSchema: DEFAULT_FILENAME_SCHEMA
    };
  }

  return {
    ...shared,
    onComplete: { action: 'doNothing' },
    onFailure: { action: 'doNothing' },
    importDetails: { type_id: '' }
  };
}

const SHARED_KEYS = ['companyName', 'kronos', 'sftp', 'directory', 'emailConfig'];

const EXPORT_OPTIONAL_KEYS = [
  'loginAsUsername',
  'encriptPGPConfig',
  'reportRetryConfig',
  'uploadRetryConfig'
];

export function parseFeedConfig(raw, direction) {
  if (raw == null || raw === '{ }' || raw === '{}' || raw === '') {
    return defaultsFor(direction);
  }
  return JSON.parse(raw);
}

// Keeps connection, client, and notification fields, and drops keys the other
// direction's schema rejects. Existing optional export settings are copied
// through when they are already present.
export function configForDirection(direction, current = {}) {
  const base = defaultsFor(direction);
  const next = { ...base };
  for (const key of SHARED_KEYS) {
    if (current[key] !== undefined) {
      next[key] = current[key];
    }
  }

  if (direction === 'export') {
    if (current.reportID !== undefined) next.reportID = current.reportID;
    if (typeof current.isExportReport === 'boolean') {
      next.isExportReport = current.isExportReport;
    }
    if (current.fileNamingSchema) next.fileNamingSchema = current.fileNamingSchema;
    for (const key of EXPORT_OPTIONAL_KEYS) {
      if (current[key] !== undefined) next[key] = current[key];
    }
    return next;
  }

  next.onComplete = current.onComplete || { action: 'doNothing' };
  next.onFailure = current.onFailure || { action: 'doNothing' };
  next.importDetails = current.importDetails || { type_id: '' };
  return next;
}

export function isConcealedSecret(value) {
  return value !== null && typeof value === 'object';
}

export function secretPresent(value) {
  if (typeof value === 'string') return value.trim() !== '';
  return isConcealedSecret(value);
}

function sftpEventErrors(label, event) {
  const errors = [];
  const action = event?.action;
  if (!action) {
    errors.push(`${label} action is required`);
    return errors;
  }
  if (!['doNothing', 'delete', 'moveTo'].includes(action)) {
    errors.push(`${label} action is not supported`);
  }
  if (action === 'moveTo' && !event.directory?.trim()) {
    errors.push(`${label} folder is required when moving the file`);
  }
  return errors;
}

export function kronosCredentialErrors(kronos = {}) {
  const errors = [];
  if (kronos.type === '1password_item') {
    if (!kronos['1password_item_path']?.trim()) {
      errors.push('Kronos 1Password item path is required');
    }
  } else {
    if (!kronos.companyName?.trim()) errors.push('Kronos company name is required');
    if (!kronos.username?.trim()) errors.push('Kronos username is required');
    if (!secretPresent(kronos.apiKey)) errors.push('Kronos API key is required');
    if (!secretPresent(kronos.password)) errors.push('Kronos password is required');
    if (!kronos.baseUrl?.trim()) errors.push('Kronos base URL is required');
  }
  return errors;
}

export function sftpCredentialErrors(sftp = {}) {
  const errors = [];
  if (sftp.type === '1password_item') {
    if (!sftp['1password_item_path']?.trim()) {
      errors.push('SFTP 1Password item path is required');
    }
  } else {
    if (!sftp.host?.trim()) errors.push('SFTP host is required');
    if (sftp.port === '' || sftp.port === undefined || sftp.port === null) {
      errors.push('SFTP port is required');
    }
    if (!sftp.username?.trim()) errors.push('SFTP username is required');
    if (!secretPresent(sftp.password) && !secretPresent(sftp.privateKey)) {
      errors.push('SFTP password or private key is required');
    }
  }
  return errors;
}

export function validateSftpFeed(values) {
  try {
    const direction = directionForJobType(values?.jobType);
    const config = parseFeedConfig(values?.config, direction);
    const errors = [];

    if (!values?.jobName?.trim()) errors.push('Job name is required');
    if (!config.companyName?.trim()) errors.push('Company short name is required');

    errors.push(...kronosCredentialErrors(config.kronos || {}));
    errors.push(...sftpCredentialErrors(config.sftp || {}));

    if (!config.directory?.trim()) errors.push('File directory is required');

    if (direction === 'import') {
      if (!config.importDetails?.type_id?.trim()) {
        errors.push('Import type ID is required');
      }
      if (typeof config.importDetails?.company_id !== 'number' || Number.isNaN(config.importDetails.company_id)) {
        errors.push('UKG company ID is required');
      }
      errors.push(...sftpEventErrors('After a successful import', config.onComplete));
      errors.push(...sftpEventErrors('After a failed import', config.onFailure));
    } else {
      if (!config.reportID?.trim()) errors.push('Report ID is required');
      if (typeof config.isExportReport !== 'boolean') {
        errors.push('Choose whether this is an export report');
      }
      if (!config.fileNamingSchema?.trim()) errors.push('File name pattern is required');
      if (config.encriptPGPConfig?.isEnabled && !secretPresent(config.encriptPGPConfig.publicKey)) {
        errors.push('PGP public key is required when encryption is on');
      }
    }

    if (values?.runOnSchedule && !values?.schedule?.trim()) {
      errors.push('Choose a schedule or turn the schedule off');
    }

    if (values?.status === 'enabled' && !values?.sftpConnectionVerified) {
      errors.push('Test the SFTP connection before activating the feed');
    }

    return errors;
  } catch (error) {
    return ['Configuration is not valid JSON'];
  }
}
